import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { rand, dateLabel, StatusPill } from '@/components/stokvel/format';
import { MembershipDecisionDialog, ExitDialog, payrollPeriodLabel } from './StokvelMemberDialogs';
import { LedgerDialog } from './StokvelLedgerDialog';

const CompanyCell = ({ m, onSaved }) => {
  const [v, setV] = useState(String(m.company_amount ?? 0));
  const save = async () => {
    if (Number(v) === Number(m.company_amount ?? 0)) return;
    try { await axios.put(`${API}/stokvel/admin/memberships/${m.id}`, { company_amount: Number(v) || 0 }); toast.success('Company contribution updated (future months)'); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  return <Input className="h-8 w-24 ml-auto text-right" type="number" min="0" value={v} onChange={e => setV(e.target.value)} onBlur={save} data-testid={`stokvel-company-${m.id}`} />;
};

export const StokvelMembersPanel = () => {
  const [rows, setRows] = useState([]);
  const [exitTypes, setExitTypes] = useState({});
  const [decide, setDecide] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [exiting, setExiting] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([axios.get(`${API}/stokvel/admin/memberships`), axios.get(`${API}/stokvel/settings`)]);
      setRows(m.data); setExitTypes(s.data.exit_types || {});
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load memberships'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reopen = async (r) => {
    if (!window.confirm(`Reopen ${r.name}'s membership? Monthly deductions resume.`)) return;
    try { await axios.post(`${API}/stokvel/admin/memberships/${r.id}/reopen`); toast.success('Membership reopened'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Reopen failed'); }
  };

  const pending = rows.filter(r => r.status === 'pending');
  const others = rows.filter(r => r.status !== 'pending');

  return (
    <div className="space-y-6">
      <section>
        <h3 className="font-semibold text-stone-800 mb-2">Pending applications ({pending.length})</h3>
        {pending.length ? pending.map(r => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border border-amber-200 bg-amber-50 rounded-lg p-3 mb-2" data-testid={`stokvel-app-${r.id}`}>
            <div className="text-sm">
              <b>{r.name}</b> {r.employee_code && <span className="text-stone-500">({r.employee_code})</span>} · {rand(r.monthly_amount)}/month
              <div className="text-xs text-stone-600">Applied {dateLabel(r.applied_at)} · signed "{r.signature_name}"{r.id_number ? ` · ID ${r.id_number}` : ''} · T&amp;Cs {r.tc_version}</div>
            </div>
            <Button size="sm" onClick={() => setDecide(r)} data-testid={`stokvel-review-${r.id}`} className="bg-stone-800 text-white hover:bg-stone-900">Review</Button>
          </div>
        )) : <p className="text-sm text-stone-500">No pending applications.</p>}
      </section>
      <section>
        <h3 className="font-semibold text-stone-800 mb-2">Members — {new Date().getFullYear()} cycle</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="stokvel-members-table">
            <thead className="bg-stone-100 text-xs uppercase text-stone-600">
              <tr>
                <th className="text-left px-2 py-2">Name</th><th className="text-left px-2 py-2">Status</th>
                <th className="text-right px-2 py-2">Deduction</th><th className="text-right px-2 py-2">Company /mo</th>
                <th className="text-right px-2 py-2">Capital</th><th className="text-right px-2 py-2">Company</th>
                <th className="text-right px-2 py-2">Interest</th><th className="text-right px-2 py-2">Withdrawn</th>
                <th className="text-right px-2 py-2">Penalty pot share</th><th className="text-right px-2 py-2">Indicative payout</th><th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {others.map(r => {
                const s = r.summary || {}; const t = s.totals || {};
                return (
                  <tr key={r.id} data-testid={`stokvel-member-${r.id}`}>
                    <td className="px-2 py-2 font-medium">{r.name}
                      {r.first_month && <div className="text-[11px] text-stone-500">from {payrollPeriodLabel(r.first_month)}</div>}
                    </td>
                    <td className="px-2 py-2"><StatusPill status={r.status} />
                      {r.status === 'declined' && <div className="text-xs text-rose-700">{r.decision_reason}</div>}
                      {r.status === 'ended' && <div className="text-[11px] text-stone-500">{r.exit?.label || r.end_reason}{r.last_month ? ` · last ${r.last_month}` : ''}</div>}
                    </td>
                    <td className="px-2 py-2 text-right font-mono">{rand(r.monthly_amount)}</td>
                    <td className="px-2 py-2 text-right">{r.status === 'active' ? <CompanyCell m={r} onSaved={load} /> : <span className="font-mono">{rand(r.company_amount)}</span>}</td>
                    <td className="px-2 py-2 text-right font-mono">{rand(t.contribution)}</td>
                    <td className="px-2 py-2 text-right font-mono">{rand(t.company)}</td>
                    <td className="px-2 py-2 text-right font-mono">{rand(t.interest)}{s.forfeited > 0 && <div className="text-[11px] text-rose-700">forfeited {rand(s.forfeited)}</div>}</td>
                    <td className="px-2 py-2 text-right font-mono">{rand((t.withdrawal || 0) + (t.refund || 0))}{t.penalty > 0 && <div className="text-[11px] text-rose-700">penalty {rand(t.penalty)}</div>}</td>
                    <td className="px-2 py-2 text-right font-mono">{rand(s.pot_share)}</td>
                    <td className="px-2 py-2 text-right font-mono font-semibold" data-testid={`stokvel-payout-${r.id}`}>{rand(s.indicative_payout)}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      {r.status !== 'declined' && <Button size="sm" variant="ghost" onClick={() => setLedger(r)} data-testid={`stokvel-ledger-${r.id}`}>Ledger</Button>}
                      {(r.status === 'active' || (r.status === 'ended' && !r.exit)) && <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => setExiting(r)} data-testid={`stokvel-exit-${r.id}`}>Exit</Button>}
                      {r.status === 'ended' && <Button size="sm" variant="ghost" onClick={() => reopen(r)} data-testid={`stokvel-reopen-${r.id}`}>Reopen</Button>}
                    </td>
                  </tr>
                );
              })}
              {!others.length && <tr><td colSpan={11} className="text-center text-stone-400 py-6">No members yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
      <MembershipDecisionDialog item={decide} onClose={() => setDecide(null)} onDone={load} />
      <LedgerDialog item={ledger} onClose={() => setLedger(null)} onDone={load} />
      <ExitDialog item={exiting} exitTypes={exitTypes} onClose={() => setExiting(null)} onDone={load} />
    </div>
  );
};
